import { deleteAllIndexedDbDatabases } from '@/utils/indexedDb'

import { storeAuthData } from './authData'
import { getServer } from './serverClient'
import { Session, clearSession } from './session'
import { getLoginCallbackUrl, getLogoutCallbackUrl } from './utils'
import { AuthLoginParameters, AuthSettings } from './types'

export const login =
    (authSettings: AuthSettings) =>
    async ({ returnTo, flow }: AuthLoginParameters = {}) => {
        const { issuer, clientId, handleRedirectToLoginFlow } = authSettings
        const { state, codeChallenge, codeChallengeMethod } = await storeAuthData(returnTo)

        const authUrl = new URL(getServer(issuer).authorization_endpoint!)
        authUrl.searchParams.set('client_id', clientId)
        authUrl.searchParams.set('state', state)
        authUrl.searchParams.set('code_challenge', codeChallenge)
        authUrl.searchParams.set('code_challenge_method', codeChallengeMethod)
        authUrl.searchParams.set('redirect_uri', getLoginCallbackUrl(authSettings))
        authUrl.searchParams.set('response_type', 'code')
        authUrl.searchParams.set('scope', 'openid email profile offline_access')
        if (flow) {
            authUrl.searchParams.set('flow', flow)
        }

        if (handleRedirectToLoginFlow) {
            await handleRedirectToLoginFlow(authUrl.toString())
        } else {
            window.location.replace(authUrl.toString())
        }

        // Wait to stop code execution since we're reloading anyway
        await new Promise((resolve) => setTimeout(resolve, 5000))
    }

export const logout =
    (authSettings: AuthSettings, session: Session) =>
    async ({ onlyClearSession }: { onlyClearSession?: boolean } = {}) => {
        const { issuer, handleRedirectToLogout } = authSettings

        const idToken = session.idToken

        clearSession(authSettings)

        if (!onlyClearSession) {
            localStorage.clear()

            const logoutUrl = new URL(getServer(issuer).end_session_endpoint!)

            // Delete all indexedDbs
            await deleteAllIndexedDbDatabases()

            if (idToken) {
                logoutUrl.searchParams.set('id_token_hint', idToken)
            }

            logoutUrl.searchParams.set('post_logout_redirect_uri', getLogoutCallbackUrl(authSettings))

            if (handleRedirectToLogout) {
                await handleRedirectToLogout(logoutUrl.toString())
            } else {
                window.location.replace(logoutUrl.toString())
            }

            // Wait to stop code execution since we're reloading anyway
            await new Promise((resolve) => setTimeout(resolve, 5000))
        }
    }
